import { Fragment, useState } from 'react';
import { useQuery } from 'react-query';
import { format } from 'date-fns';

import { Typography } from '@app/components';
import { clsxm } from '@app/styles/clsxm';
import { FORMAT } from '@app/constants/formats';
import { EmailApi } from '@app/domain/email/api/EmailApi';
import { EmailLogEntryOutDto } from '@app/swagger-types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { StatusPill, StatusPillVariant } from '../../shared/StatusPill';

const STATUS_VARIANT: Record<string, StatusPillVariant> = {
  delivered: 'ok',
  sent: 'mut',
  bounced: 'bad',
  blocked: 'warn',
  complained: 'bad',
  dropped: 'info',
  skipped: 'mut',
};

const STATUS_LABEL: Record<string, string> = {
  delivered: 'Delivered',
  sent: 'Sent',
  bounced: 'Bounce',
  blocked: 'Deferred',
  complained: 'Complaint',
  dropped: 'Dropped',
  skipped: 'Skipped',
};

// admin-emailbeheer blok B (opdracht §6, Scherm 3). "De JSON-contextkolom... gaat naar een
// uitklapbaar detailpaneel per regel" - fetched on expand, not loaded for every row up front.
export const EmailLogTable: React.FC<{ rows: EmailLogEntryOutDto[]; loading?: boolean }> = ({ rows, loading }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="overflow-x-auto rounded-md bg-white pt-1 shadow-card">
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="border-b border-gray-200 text-gray-400">
            <th className="w-8 py-2 pl-3" />
            <th className="py-2 pr-3">Timestamp</th>
            <th className="py-2 pr-3">Recipient</th>
            <th className="py-2 pr-3">Mail key</th>
            <th className="py-2 pr-3">Locale</th>
            <th className="py-2 pr-3">Status</th>
            <th className="py-2 pr-3">Details</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const rowKey = `${row.recordType}-${row.recordId}`;
            const isExpanded = expandedId === rowKey;
            return (
              <Fragment key={rowKey}>
                <tr
                  className={clsxm(
                    'cursor-pointer hover:bg-accept-light',
                    i % 2 === 1 && 'bg-gray-50',
                    !row.hasContext && 'cursor-default'
                  )}
                  onClick={() => row.hasContext && setExpandedId(isExpanded ? null : rowKey)}
                >
                  <td className="py-1.5 pl-3">
                    {row.hasContext &&
                      (isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />)}
                  </td>
                  <td className="py-1.5 pr-3">{row.at ? format(new Date(row.at), FORMAT.FULL_TIME) : '-'}</td>
                  <td className="py-1.5 pr-3">{row.recipientEmail || '-'}</td>
                  <td className="font-mono py-1.5 pr-3">{row.mailKey}</td>
                  <td className="py-1.5 pr-3">{row.locale || '-'}</td>
                  <td className="py-1.5 pr-3">
                    <StatusPill variant={STATUS_VARIANT[row.status] ?? 'mut'}>
                      {STATUS_LABEL[row.status] ?? row.status}
                    </StatusPill>
                  </td>
                  <td className="py-1.5 pr-3 text-gray-500">{row.details || '-'}</td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={7} className="bg-gray-50 p-0">
                      <ContextDetailPanel recordType={row.recordType} recordId={row.recordId} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          {!loading && !rows.length && (
            <tr>
              <td colSpan={7} className="py-6 text-center text-gray-400">
                No mail in this period.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const ContextDetailPanel: React.FC<{ recordType: EmailLogEntryOutDto['recordType']; recordId: string }> = ({
  recordType,
  recordId,
}) => {
  const contextQuery = useQuery(['email-log-context', recordType, recordId], () =>
    EmailApi.adminGetEmailLogContext(recordType, recordId)
  );

  const pretty = (() => {
    if (!contextQuery.data) {
      return '';
    }
    // axios parses any JSON-shaped response body by default regardless of Content-Type, and
    // `context` is always stored as a JSON string - so despite the string-typed API, this can
    // already be a plain object at runtime. JSON.parse(object) doesn't throw a helpful error,
    // it stringifies the object first ("[object Object]") and then fails to parse that, so the
    // old code's catch block ended up returning the raw object - React error #31 (found
    // 2026-08-24, crashed on any row whose context happened to parse as JSON, i.e. always).
    if (typeof contextQuery.data === 'object') {
      return JSON.stringify(contextQuery.data, null, 2);
    }
    try {
      return JSON.stringify(JSON.parse(contextQuery.data), null, 2);
    } catch {
      return contextQuery.data;
    }
  })();

  return (
    <div className="p-3">
      {contextQuery.isLoading ? (
        <Typography variant="xs" className="text-gray-400">
          Loading...
        </Typography>
      ) : (
        <pre className="max-h-64 overflow-auto rounded bg-white p-2 text-xs">{pretty || 'No context stored.'}</pre>
      )}
    </div>
  );
};
